import { useTranslation } from "react-i18next";

import type { SudPoint } from "../../domain/emdrSummary";

const WIDTH = 320;
const HEIGHT = 120;
const PAD = { top: 10, right: 12, bottom: 22, left: 26 };

/**
 * How disturbing the target felt, set by set: the curve a therapist reads to
 * see whether reprocessing is working. Drawn small and plain — one line on a
 * 0–10 scale — with the numbers in the table beside it for anyone who cannot
 * see it.
 */
export default function SudCurve({ points }: { points: SudPoint[] }) {
    const { t } = useTranslation();
    if (points.length < 2) return null;

    const innerW = WIDTH - PAD.left - PAD.right;
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const x = (i: number) => PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
    const y = (sud: number) => PAD.top + (1 - sud / 10) * innerH;
    const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.sud).toFixed(1)}`).join(" ");
    const description = points.map((p) => `${p.setNumber}: ${p.sud}`).join(", ");

    return (
        <figure className="flex flex-col gap-1">
            <svg
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                role="img"
                aria-label={t("emdr.session.curveLabel")}
                aria-describedby="emdr-sud-curve-desc"
                className="h-auto w-full max-w-md text-accent-text"
            >
                {[0, 5, 10].map((tick) => (
                    <g key={tick}>
                        <line
                            x1={PAD.left}
                            x2={WIDTH - PAD.right}
                            y1={y(tick)}
                            y2={y(tick)}
                            className="stroke-border-subtle"
                            strokeWidth={1}
                        />
                        <text x={PAD.left - 6} y={y(tick) + 3} textAnchor="end" className="fill-content-tertiary text-[9px]">
                            {tick}
                        </text>
                    </g>
                ))}
                <path d={path} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                {points.map((p, i) => (
                    <g key={p.setNumber}>
                        <circle cx={x(i)} cy={y(p.sud)} r={3} fill="currentColor" />
                        <text x={x(i)} y={HEIGHT - 6} textAnchor="middle" className="fill-content-tertiary text-[9px]">
                            {p.setNumber}
                        </text>
                    </g>
                ))}
            </svg>
            <figcaption id="emdr-sud-curve-desc" className="sr-only">
                {description}
            </figcaption>
        </figure>
    );
}
